/**
 * Ayarlar › API kullanımı (v0.1.164, specs/api-kullanimi.md): every outside API on one page. On top the month's real
 * spend, today's, the Claude value apart (never billed) and how many APIs are near or at a limit; then one block per
 * provider: name, purpose, state (shape and word, colour third), Bugün, Bu ay, Kalan with when and where it was read,
 * the money where the ledger has it, and a 30 day bar chart whose title and hidden table read the numbers. A provider
 * that reports nothing left says so; nothing is guessed. Phone (one column, 360 px) and desktop (two columns).
 */

import { useEffect } from 'react';
import {
  API_USAGE_STATUS_LABELS,
  type ApiUsageProviderView,
  type ApiUsageQuota,
  type ApiUsageStatus,
  type ApiUsageView,
  lastErrorText,
  QUOTA_SOURCE_LABELS,
  QUOTA_UNIT_LABELS,
  QUOTA_WINDOW_LABELS,
} from '../api/api-usage.js';
import type { ConnectionStatus } from '../api/settings.js';
import { useApiUsage } from '../app/api-usage.js';
import { usePanel } from '../app/context.js';
import { formatIsoDay, formatNumber, formatRelativeClock, formatUsd } from '../lib/format.js';
import { BarValueLabels, ChartFrame, ChartReadout, pickHandlers, useChartPick } from './chart-kit.js';
import { Icon } from './icons.js';
import { StatusGlyph } from './settings.js';

/** The connection pill shape each state borrows (Ayarlar › Bağlantılar). */
const STATUS_SHAPE: Readonly<Record<ApiUsageStatus, ConnectionStatus>> = Object.freeze({
  connected: 'connected',
  key_missing: 'not_connected',
  off: 'disabled',
  not_in_twin: 'disabled',
});

function StatusChip({ status }: { status: ApiUsageStatus }) {
  const shape = STATUS_SHAPE[status];
  return (
    <span className={`cpill cpill--${shape} au-chip`}>
      <StatusGlyph status={shape} inPill />
      {API_USAGE_STATUS_LABELS[status]}
    </span>
  );
}

function amount(value: number, unit: ApiUsageQuota['unit']): string {
  switch (unit) {
    case 'usd':
      return formatUsd(Math.round(value * 1_000_000));
    case 'percent':
      return `%${formatNumber(value, Number.isInteger(value) ? 0 : 1)}`;
    default:
      return `${formatNumber(value, Number.isInteger(value) ? 0 : 1)} ${QUOTA_UNIT_LABELS[unit]}`;
  }
}

/** The figure without its window: "1.100 / 20.000 istek kaldı", "%35 kullanıldı, %65 kaldı", "1.180 kredi kaldı". */
function figureText(quota: ApiUsageQuota): string {
  if (quota.unit === 'percent') {
    if (quota.used === null) {
      // A Claude event with a status and no percentage (at the limit, or warned).
      return quota.remaining === null ? 'yüzde bildirilmedi' : `${amount(quota.remaining, 'percent')} kaldı`;
    }
    const left = quota.remaining === null ? '' : `, ${amount(quota.remaining, 'percent')} kaldı`;
    return `${amount(quota.used, 'percent')} kullanıldı${left}`;
  }
  if (quota.remaining !== null && quota.limit !== null) {
    const limit = quota.unit === 'usd' ? amount(quota.limit, 'usd') : formatNumber(quota.limit);
    const left = quota.unit === 'usd' ? amount(quota.remaining, 'usd') : formatNumber(quota.remaining);
    return `${left} / ${limit}${quota.unit === 'usd' ? '' : ` ${QUOTA_UNIT_LABELS[quota.unit]}`} kaldı`;
  }
  if (quota.remaining !== null) return `${amount(quota.remaining, quota.unit)} kaldı`;
  if (quota.used !== null) return `${amount(quota.used, quota.unit)} harcandı, kalan bilinmiyor`;
  return 'bilinmiyor';
}

/** "aylık: 1.100 / 20.000 istek kaldı", "5 saatlik: %35 kullanıldı, %65 kaldı". */
function quotaText(quota: ApiUsageQuota): string {
  return `${QUOTA_WINDOW_LABELS[quota.window]}: ${figureText(quota)}`;
}

/** When a reset window gets its new figure: with the next answer, or with the next balance or usage read. */
function nextReadText(source: ApiUsageQuota['source']): string {
  return source === 'balance_api' || source === 'usage_api'
    ? 'yeni değer bir sonraki okumada (3 saatte bir) gelir'
    : 'yeni değer bir sonraki çağrıda okunur';
}

const OWN_SOURCES: ReadonlySet<ApiUsageQuota['source']> = new Set(['response_header', 'balance_api', 'usage_api']);

const LEVEL_WORDS: Readonly<Record<ApiUsageQuota['level'], string | null>> = Object.freeze({
  ok: null,
  near: 'sınıra yakın',
  over: 'bitti',
  unknown: null,
});

function QuotaLine({ quota, now, providerName }: { quota: ApiUsageQuota; now: number; providerName: string }) {
  const level = LEVEL_WORDS[quota.level];
  // A provider's own answer names the provider ("Brave Search yanıt başlığı"); a GM run or a hand entry does not.
  const where = OWN_SOURCES.has(quota.source)
    ? `${providerName} ${QUOTA_SOURCE_LABELS[quota.source]}`
    : QUOTA_SOURCE_LABELS[quota.source];
  const readAt = formatRelativeClock(Date.parse(quota.capturedAt), now);
  if (quota.expired === true) {
    // The window reset after this reading: the last figure is history, not what is left now.
    return (
      <li className="au-quota au-quota--unknown au-quota--expired">
        <span className="au-quota__value">
          {QUOTA_WINDOW_LABELS[quota.window]}: pencere yenilendi; {nextReadText(quota.source)}
        </span>
        <span className="au-quota__meta">
          son okuma {readAt}: {figureText(quota)} · {where}
        </span>
        {quota.detailTr === null ? null : <span className="au-quota__detail">{quota.detailTr}</span>}
      </li>
    );
  }
  const read = quota.source === 'twin_cap' ? QUOTA_SOURCE_LABELS.twin_cap : `okundu ${readAt} · ${where}`;
  const reset = quota.resetsAt === null ? null : `yenilenir ${formatRelativeClock(Date.parse(quota.resetsAt), now)}`;
  return (
    <li className={`au-quota au-quota--${quota.level}`}>
      <span className="au-quota__value">
        {quotaText(quota)}
        {level === null ? null : <span className={`au-level au-level--${quota.level}`}>{level}</span>}
      </span>
      <span className="au-quota__meta">{reset === null ? read : `${read} · ${reset}`}</span>
      {quota.detailTr === null ? null : <span className="au-quota__detail">{quota.detailTr}</span>}
    </li>
  );
}

function countText(count: { calls: number; errors: number }, unit: string): string {
  const errors = count.errors > 0 ? ` · ${formatNumber(count.errors)} hata` : '';
  return `${formatNumber(count.calls)} ${unit}${errors}`;
}

/**
 * The 30 days as bars: the failed part of a day sits on top in the danger colour; no calls at all is one line. The
 * busiest and the last day carry their count, a tap, a click or the arrow keys pick a day for the readout row
 * (audit chart rules, 25 Oct 2026); bars in the neutral data colour, never the accent.
 */
export function UsageBars({ provider }: { provider: ApiUsageProviderView }) {
  const daily = provider.daily;
  const pick = useChartPick(daily.length);
  const total = daily.reduce((sum, day) => sum + day.calls, 0);
  if (total === 0) return <p className="au-bars__empty">Son 30 günde {provider.unitTr} yok.</p>;
  const width = 300;
  const height = 56;
  const gap = 2;
  const max = Math.max(1, ...daily.map((day) => day.calls));
  const bar = (width - gap * (daily.length - 1)) / Math.max(1, daily.length);
  const errors = daily.reduce((sum, day) => sum + day.errors, 0);
  const peak = daily.reduce<(typeof daily)[number] | null>(
    (best, day) => (best === null || day.calls > best.calls ? day : best),
    null,
  );
  const first = daily[0]?.day ?? '';
  const last = daily.at(-1)?.day ?? '';
  const titleId = `au-${provider.id}-bars`;
  const title = `${provider.nameTr}: son 30 günde ${formatNumber(total)} ${provider.unitTr}, ${formatNumber(errors)} hata; en çok ${formatIsoDay(peak?.day ?? '')} (${formatNumber(peak?.calls ?? 0)})`;
  const shown = daily[pick.index];
  const readout =
    shown === undefined
      ? ''
      : `${formatIsoDay(shown.day)}: ${formatNumber(shown.calls)} ${provider.unitTr}${shown.errors > 0 ? ` · ${formatNumber(shown.errors)} hata` : ''}`;
  return (
    <figure className="au-bars">
      <p className="au-bars__takeaway">
        30 günde {formatNumber(total)} {provider.unitTr}
        {errors > 0 ? `, ${formatNumber(errors)} hata` : ', hata yok'} · en çok {formatIsoDay(peak?.day ?? '')}
      </p>
      <ChartFrame
        label={`${provider.nameTr}, son 30 gün: gün seç`}
        count={daily.length}
        pick={pick}
        valueText={readout}
      >
        <div className="chart-plot">
          <svg
            className="au-bars__svg"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>{title}</title>
            <line className="au-bars__base" x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} />
            {daily.map((day, index) => {
              const x = index * (bar + gap);
              const full = (day.calls / max) * (height - 2);
              const failed = day.calls === 0 ? 0 : (day.errors / day.calls) * full;
              return (
                <g key={day.day} className={pick.index === index ? 'is-picked' : undefined}>
                  <rect
                    className="chart-hit"
                    x={x}
                    y={0}
                    width={bar + gap}
                    height={height}
                    {...pickHandlers(pick, index)}
                  />
                  {full - failed > 0 ? (
                    <rect
                      className="au-bars__ok"
                      x={x}
                      y={height - 1 - full + failed}
                      width={bar}
                      height={full - failed}
                      pointerEvents="none"
                    />
                  ) : null}
                  {failed > 0 ? (
                    <rect
                      className="au-bars__err"
                      x={x}
                      y={height - 1 - full}
                      width={bar}
                      height={failed}
                      pointerEvents="none"
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>
          <BarValueLabels values={daily.map((day) => day.calls)} max={max} text={formatNumber} />
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(first)}</span>
        <span>{formatIsoDay(last)}</span>
      </figcaption>
      {/* A table ignores the 1 px width of .sr-only; the wrapper clips it so the page cannot scroll sideways. */}
      <div className="sr-only">
        <table>
          <caption>{provider.nameTr}: son 30 gün, gün gün</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              <th scope="col">{provider.unitTr}</th>
              <th scope="col">Hata</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                <td>{day.calls}</td>
                <td>{day.errors}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

const PROVIDER_SOURCES: ReadonlySet<ApiUsageQuota['source']> = new Set([...OWN_SOURCES, 'rate_limit_event']);

/**
 * Why Kalan has no reading of the provider's own: outside Twin, a provider that never reports, no key, or a provider
 * that reports but has not been read yet (fresh deploy, no call yet, a failed read). Null when there is a reading.
 */
function missingQuotaText(provider: ApiUsageProviderView): string | null {
  if (provider.status === 'not_in_twin') return provider.quotas.length === 0 ? 'Twin’de kayıt yok' : null;
  if (provider.quotaReported === 'never') {
    return provider.quotas.length === 0 ? 'Sağlayıcı bildirmiyor; Twin tahmin etmez.' : null;
  }
  if (provider.quotas.some((quota) => PROVIDER_SOURCES.has(quota.source))) return null;
  if (provider.status === 'key_missing') return 'Anahtar girilmedi; okunamıyor';
  return provider.quotaReported === 'header'
    ? 'Henüz okunmadı: ilk çağrıdan sonra görünür'
    : 'Henüz okunmadı: 3 saatte bir sorulur';
}

function ProviderBlock({ provider, now }: { provider: ApiUsageProviderView; now: number }) {
  const headingId = `au-${provider.id}-h`;
  const outside = provider.status === 'not_in_twin';
  const missing = missingQuotaText(provider);
  const money = (micro: number | null) =>
    micro === null ? null : (
      <>
        {' · '}
        <span className="mono">{formatUsd(micro)}</span>
      </>
    );
  return (
    <article className={`au-card au-card--${provider.status}`} aria-labelledby={headingId}>
      <header className="au-card__head">
        <div className="au-card__titles">
          <h3 id={headingId} className="au-card__name">
            {provider.nameTr}
          </h3>
          <p className="au-card__purpose">{provider.purposeTr}</p>
        </div>
        <StatusChip status={provider.status} />
      </header>
      <dl className="au-card__nums">
        {outside ? null : (
          <>
            <div className="au-num">
              <dt>Bugün</dt>
              <dd>
                {countText(provider.today, provider.unitTr)}
                {money(provider.todayMicroUsd)}
              </dd>
            </div>
            <div className="au-num">
              <dt>Bu ay</dt>
              <dd>
                {countText(provider.month, provider.unitTr)}
                {money(provider.monthMicroUsd)}
                {provider.notionalMonthMicroUsd === null ? null : (
                  <span className="au-num__notional">
                    {formatUsd(provider.notionalMonthMicroUsd)} karşılık, faturalanmaz
                  </span>
                )}
              </dd>
            </div>
          </>
        )}
        <div className="au-num au-num--left">
          <dt>Kalan</dt>
          <dd>
            {missing === null ? null : <span className="au-quota__none">{missing}</span>}
            {provider.quotas.length === 0 ? null : (
              <ul className="au-quotas">
                {provider.quotas.map((quota) => (
                  <QuotaLine
                    key={`${quota.source}-${quota.window}`}
                    quota={quota}
                    now={now}
                    providerName={provider.nameTr}
                  />
                ))}
              </ul>
            )}
          </dd>
        </div>
      </dl>
      {outside ? null : <UsageBars provider={provider} />}
      <div className="au-card__foot">
        {provider.noteTr === null ? null : <p className="au-card__note">{provider.noteTr}</p>}
        {provider.lastError === null ? null : (
          <p className="au-card__error">
            Son hata: <span title={provider.lastError.kind}>{lastErrorText(provider.lastError.kind)}</span>,{' '}
            {formatRelativeClock(Date.parse(provider.lastError.at), now)}
          </p>
        )}
        {provider.lastCallAt === null || outside ? null : (
          <p className="au-card__last">Son çağrı {formatRelativeClock(Date.parse(provider.lastCallAt), now)}</p>
        )}
        {provider.linkUrl === null ? null : (
          <a className="au-card__link" href={provider.linkUrl} target="_blank" rel="noreferrer">
            {provider.nameTr} paneli
            <Icon name="external-link" size={14} />
          </a>
        )}
      </div>
    </article>
  );
}

function UsageSummary({
  view,
  loading,
  error,
  onReload,
}: {
  view: ApiUsageView;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  const summary = view.summary;
  const clock = usePanel().now;
  const limits =
    summary.overLimit === 0 && summary.nearLimit === 0
      ? 'Sınıra yakın API yok'
      : `${summary.overLimit} API sınırda, ${summary.nearLimit} API sınıra yakın`;
  return (
    <section className="au-sum drafting" aria-labelledby="au-sum-h">
      <h2 id="au-sum-h" className="au-sum__title">
        Bu ay gerçek harcama <span className="mono accent">{formatUsd(summary.realMonthMicroUsd)}</span>
      </h2>
      <dl className="au-sum__grid">
        <div>
          <dt>Bugün</dt>
          <dd className="mono">{formatUsd(summary.realTodayMicroUsd)}</dd>
        </div>
        <div>
          <dt>GM aboneliği karşılığı (faturalanmaz)</dt>
          <dd className="mono">{formatUsd(summary.notionalMonthMicroUsd)}</dd>
        </div>
        <div>
          <dt>Sınırlar</dt>
          <dd>{limits}</dd>
        </div>
        <div>
          <dt>Kalanı bildirmeyen</dt>
          <dd>{summary.noQuota} API</dd>
        </div>
      </dl>
      <p className="au-sum__meta">
        <span>Güncellendi {formatRelativeClock(Date.parse(view.generatedAt), clock)}</span>
        <button type="button" className="btn btn--secondary btn--small" onClick={onReload} disabled={loading}>
          <Icon name="refresh-cw" size={14} />
          {loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </p>
      {error === null ? null : (
        <p className="au-sum__error" role="status">
          Yenilenemedi: {error}
        </p>
      )}
    </section>
  );
}

export function ApiUsageBody({ desktop = false }: { desktop?: boolean }) {
  const { store, state } = useApiUsage();
  useEffect(() => {
    // Read on every open: the last view stays on screen while the new one loads (the panel stays open for days).
    void store.reload();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        API kullanımı yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  const now = Date.parse(view.generatedAt);
  return (
    <div className={desktop ? 'au au--desk' : 'au'}>
      <UsageSummary view={view} loading={state.loading} error={state.error} onReload={() => void store.reload()} />
      <ul className="au-list" aria-label="API’ler">
        {view.providers.map((provider) => (
          <li key={provider.id}>
            <ProviderBlock provider={provider} now={now} />
          </li>
        ))}
      </ul>
      <p className="au-foot">
        Günler UTC’ye göre (Türkiye saatiyle 03:00’te döner). Sayılmayanlar: sıradan web sayfaları, arşivler, RDAP ve
        Dağıtıcı’nın kendi çağrıları.
      </p>
    </div>
  );
}

/** The desktop rail: how to read the page. */
export function ApiUsageRail() {
  return (
    <aside className="panel st-rail" aria-labelledby="au-rail-h">
      <div className="st-rail__head">
        <h2 id="au-rail-h" className="st-rail__title">
          Nasıl okunur
        </h2>
        <span className="ink-3">kaynaklar</span>
      </div>
      <ul className="au-rail">
        <li>
          <strong>Kalan</strong> sadece sağlayıcının söylediğidir: yanıt başlığı (Brave, Etsy), bakiye ucu
          (MillionVerifier, Reoon) veya kullanım ucu (Genesis). Hiç söylemeyen için “bildirmiyor”, henüz okunmamış için
          “henüz okunmadı” yazar.
        </li>
        <li>
          <strong>Twin’in kendi sınırı</strong>: ekip tavanı ve Genesis Eye taramasında günde 40 arama. Sağlayıcının
          kotası değildir.
        </li>
        <li>
          <strong>Tutar</strong> maliyet defterindendir; GM aboneliği karşılığı faturalanmaz, toplama eklenmez.
        </li>
        <li>Kalanlar 3 saatte bir ve her yanıtta yenilenir; sayfa açılınca okunur.</li>
      </ul>
    </aside>
  );
}
